import {Button, IconButton, type ButtonProps, type IconButtonProps} from '@primer/react'
import type {IconProps} from '@primer/octicons-react'
import styles from './styles.module.css'
import {useGlobalNavAnalytics} from '../utils/Analytics'
import {clsx} from 'clsx'

export interface NavLinkItemProps {
  /** Location this link should be directed to */
  href?: string

  /** The text label for the link */
  linkText?: React.ReactNode

  /** The label for the link (will show as a tooltip) */
  'aria-label': string

  /** The icon to display within the link */
  icon: React.ComponentType<IconProps>

  /** Optional hotkey to link to this item */
  hotkey?: string

  /** Optional sub-category for analytics */
  analyticsSubCategory?: string

  /** The analytics meta-data to send along when tracking clicks */
  analyticsKey: string

  /** Additional class name(s) to apply to the button */
  className?: IconButtonProps['className']

  /** onClick handler */
  onClick?: ButtonProps['onClick']
}

/**
 * The NavLinkItem is used to display individual navigation icons in the TopNav
 */
export const NavLinkItem = (props: NavLinkItemProps) => {
  const {
    href,
    'aria-label': ariaLabel,
    icon: Icon,
    hotkey,
    analyticsKey,
    analyticsSubCategory,
    linkText,
    onClick,
  } = props
  const {sendItemClick} = useGlobalNavAnalytics(analyticsSubCategory)

  if (linkText) {
    return (
      <Button
        as="a"
        href={href}
        variant="invisible"
        aria-label={ariaLabel}
        data-hotkey={hotkey}
        className={clsx(styles.appHeaderButton, props.className)}
        onClick={e => {
          sendItemClick(analyticsKey)
          onClick?.(e)
        }}
        leadingVisual={Icon}
      >
        {linkText}
      </Button>
    )
  }

  return (
    <IconButton
      as="a"
      variant="invisible"
      icon={Icon}
      aria-label={ariaLabel}
      href={href}
      data-hotkey={hotkey}
      className={clsx(styles.appHeaderButton, props.className)}
      keybindingHint={hotkey}
      onClick={e => {
        sendItemClick(analyticsKey, ariaLabel)
        onClick?.(e)
      }}
    />
  )
}

/**
 * The NavButtonItem is used to display individual navigation buttons in the TopNav
 */
export const NavButtonItem = (props: NavLinkItemProps) => {
  const {'aria-label': ariaLabel, icon: Icon, hotkey, analyticsKey, linkText, onClick} = props
  const {sendItemClick} = useGlobalNavAnalytics()

  if (linkText) {
    return (
      <Button
        variant="invisible"
        aria-label={ariaLabel}
        data-hotkey={hotkey}
        className={clsx(styles.appHeaderButton, props.className)}
        onClick={e => {
          sendItemClick(analyticsKey)
          onClick?.(e)
        }}
        leadingVisual={Icon}
      >
        {linkText}
      </Button>
    )
  }

  return (
    <IconButton
      variant="invisible"
      icon={Icon}
      aria-label={ariaLabel}
      data-hotkey={hotkey}
      className={clsx(styles.appHeaderButton, props.className)}
      keybindingHint={hotkey}
      onClick={e => {
        sendItemClick(analyticsKey, ariaLabel)
        onClick?.(e)
      }}
    />
  )
}

try{ NavLinkItem.displayName ||= 'NavLinkItem' } catch {}
try{ NavButtonItem.displayName ||= 'NavButtonItem' } catch {}